import { useEffect, useState } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../lib/auth.jsx'
import { api } from '../lib/api.js'
import { useLive } from '../lib/live.jsx'
import { CopyButton, useAsync, useConfirm, useToast } from './Ui.jsx'
import Icon from './Icon.jsx'
import { publicDirectoryUrl } from '../lib/share.js'

/**
 * Left menu, one entry per screen the signed-in role actually owns.
 * `[path, label, icon, exact]` — the path doubles as the key the badge
 * endpoint reports counts against.
 */
const NAV = {
  admin: [
    {
      title: 'Desk',
      items: [
        ['/admin', 'Overview', 'overview', true],
        ['/operator', 'Operator desk', 'desk', true]
      ]
    },
    {
      title: 'Supply',
      items: [
        ['/admin/hatcheries', 'Hatcheries', 'hatchery'],
        ['/admin/network', 'Transport network', 'truck']
      ]
    },
    {
      title: 'Demand',
      items: [
        ['/operator/enquiries', 'Enquiries', 'enquiry'],
        ['/admin/farmers', 'Farmers', 'farmers'],
        ['/operator/orders', 'Bookings', 'orders']
      ]
    },
    {
      title: 'Platform',
      items: [
        ['/admin/share', 'Farmer link', 'link'],
        ['/admin/users', 'Logins & staff', 'key'],
        ['/admin/settings', 'Settings', 'settings']
      ]
    }
  ],
  operator: [
    {
      title: 'Desk',
      items: [
        ['/operator', 'Desk', 'desk', true],
        ['/operator/enquiries', 'Enquiries', 'enquiry'],
        ['/operator/match', 'Match & book', 'match']
      ]
    },
    {
      title: 'Running work',
      items: [
        ['/operator/orders', 'Orders', 'orders'],
        ['/operator/trips', 'Logistics', 'truck'],
        ['/operator/farmers', 'Farmers', 'farmers']
      ]
    },
    {
      title: 'Share',
      items: [['/admin/share', 'Farmer link', 'link']]
    }
  ],
  hatchery: [
    {
      title: 'My hatchery',
      items: [
        ['/hatchery', 'My batches', 'batch', true],
        ['/hatchery/orders', 'Bookings', 'orders'],
        ['/hatchery/profile', 'Hatchery profile', 'profile']
      ]
    }
  ],
  liaison: [
    {
      title: 'Field',
      items: [['/liaison', 'My trips', 'truck', true]]
    }
  ]
}

const CTA = {
  admin: ['/admin/hatcheries/new', 'Onboard hatchery'],
  operator: ['/operator/match', 'Book seed for a farmer'],
  hatchery: ['/hatchery/batches/new', 'Post a batch'],
  liaison: null
}

const ROLE_LABEL = {
  admin: 'Platform admin',
  operator: 'Operator desk',
  hatchery: 'Hatchery portal',
  liaison: 'Liaison officer'
}

const SIDEBAR_KEY = 'aqua.sidebar.collapsed'

export default function Layout () {
  const { user, signOut } = useAuth()
  const { status } = useLive()
  const navigate = useNavigate()
  const location = useLocation()
  const toast = useToast()
  const confirm = useConfirm()

  const leave = async () => {
    if (!await confirm({
      title: 'Sign out?',
      body: 'Anything you have typed but not saved is lost. You will need your password to get back in.',
      confirmLabel: 'Sign out'
    })) return
    signOut()
    navigate('/login')
  }

  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_KEY) === '1' } catch { return false }
  })
  const [drawer, setDrawer] = useState(false)

  useEffect(() => {
    try { localStorage.setItem(SIDEBAR_KEY, collapsed ? '1' : '0') } catch { /* private mode */ }
  }, [collapsed])

  // Tapping a link on a phone should close the drawer behind it.
  useEffect(() => { setDrawer(false) }, [location.pathname])

  const { data: badgeData } = useAsync(
    () => api.get('/auth/badges').catch(() => ({ badges: {} })),
    [],
    { live: ['orders', 'batches', 'enquiries', 'trips', 'hatcheries', 'farmers'] }
  )
  const badges = badgeData?.badges || {}

  const sections = NAV[user?.role] || []
  const cta = CTA[user?.role]
  const shareUrl = publicDirectoryUrl()
  const canShare = user?.role === 'admin' || user?.role === 'operator'

  // Longest matching nav path wins, so /admin/hatcheries/new still reads
  // "Hatcheries" rather than falling back to the role.
  const here = sections
    .flatMap(s => s.items)
    .filter(([to]) => location.pathname === to || location.pathname.startsWith(`${to}/`))
    .sort((a, b) => b[0].length - a[0].length)[0]

  return (
    <div className={`shell ${collapsed ? 'collapsed' : ''} ${drawer ? 'drawer' : ''}`}>
      <div className="scrim" onClick={() => setDrawer(false)} />

      <aside className="side">
        <div className="side-brand">
          <NavLink to="/" className="brand">
            <span className="glyph">🦐</span>
            <span className="brand-text">
              AQUA PLATFORM
              <small>{ROLE_LABEL[user?.role] || 'Kakinada seed desk'}</small>
            </span>
          </NavLink>
          <button className="icon-btn only-desk" onClick={() => setCollapsed(c => !c)}
            title={collapsed ? 'Expand menu' : 'Collapse menu'} aria-label="Toggle menu width">
            <Icon name="chevron" size={16} className={collapsed ? '' : 'flip'} />
          </button>
        </div>

        {cta && (
          <NavLink className="side-cta" to={cta[0]}>
            <Icon name="plus" size={16} />
            <span className="side-label">{cta[1]}</span>
          </NavLink>
        )}

        <nav className="side-nav">
          {sections.map(section => (
            <div className="side-group" key={section.title}>
              <div className="side-group-title">{section.title}</div>
              {section.items.map(([to, label, icon, exact]) => {
                const count = badges[to]
                return (
                  <NavLink key={to} to={to} end={Boolean(exact)}
                    className={({ isActive }) => `side-link ${isActive ? 'on' : ''}`} title={label}>
                    <Icon name={icon} />
                    <span className="side-label">{label}</span>
                    {count > 0 && <span className="side-count">{count > 99 ? '99+' : count}</span>}
                  </NavLink>
                )
              })}
            </div>
          ))}

          {user?.role === 'hatchery' && user?.hatchery?._id && (
            <div className="side-group">
              <div className="side-group-title">What farmers see</div>
              <a className="side-link" href={`/hatcheries/${user.hatchery._id}`} target="_blank" rel="noreferrer" title="My public page">
                <Icon name="globe" />
                <span className="side-label">My public page</span>
              </a>
            </div>
          )}
        </nav>

        <div className="side-foot">
          <NavLink className="side-user" to="/account">
            <span className="avatar">{(user?.displayName || '?').slice(0, 1).toUpperCase()}</span>
            <span className="side-label">
              <b>{user?.displayName}</b>
              <small>{user?.username}</small>
            </span>
          </NavLink>
          <button className="icon-btn" onClick={leave} title="Sign out" aria-label="Sign out">
            <Icon name="logout" size={16} />
          </button>
        </div>
      </aside>

      <div className="main">
        <header className="tbar">
          <button className="icon-btn only-mobile" onClick={() => setDrawer(d => !d)} aria-label="Open menu">
            <Icon name="menu" />
          </button>

          <div className="tbar-where">
            <b>{here ? here[1] : ROLE_LABEL[user?.role]}</b>
            <small>{ROLE_LABEL[user?.role]} · Kakinada seed desk</small>
          </div>

          <div className="spacer" />

          {canShare && (
            <CopyButton className="btn sm only-desk" value={shareUrl}
              told="Farmer link copied — paste it into WhatsApp">
              Copy farmer link
            </CopyButton>
          )}

          {/* Screens refresh themselves off the live feed whether or not anything
              says so, so there is no badge for the healthy case. A dropped feed
              is different: the page has quietly gone stale and you need to know. */}
          {status === 'retrying' && (
            <span className="stale" title="The screen is not receiving updates until this reconnects">
              <i />
              Reconnecting…
            </span>
          )}

          <button className="icon-btn only-mobile" onClick={() => {
            if (navigator.share) {
              navigator.share({ title: 'Aqua Platform hatcheries', url: shareUrl }).catch(() => {})
            } else {
              toast?.info('Open “Farmer link” in the menu to copy the link')
            }
          }} aria-label="Share">
            <Icon name="link" size={18} />
          </button>
        </header>

        <Outlet />
      </div>
    </div>
  )
}
